/**
 * useExcerptImageExport
 * ---
 * Extracted from `ExcerptImageTab.tsx`. Owns the JPG render pipeline:
 *   1. wait for the chosen font to load,
 *   2. snapshot the preview node via html-to-image (`toCanvas`),
 *   3. flatten onto an opaque background canvas (JPEG has no alpha),
 *   4. encode + stamp Excerptus metadata,
 *   5. trigger a browser download and bump the workspace usage counter.
 *
 * Returns `{ download, downloading }`. The caller still owns the upgrade
 * dialog and the preview ref.
 */
import { useState } from "react";
import { toCanvas } from "html-to-image";
import { toast } from "sonner";
import { ensureFont } from "@/lib/excerptImageGenerator";
import { embedExcerptusMetadata } from "@/lib/imageMetadata";

type Args = {
  previewRef: React.RefObject<HTMLDivElement>;
  active: { id: string } | null;
  fontFamily: string;
  exportBgFallback: string;
  downloadsAtLimit: boolean;
  isOverflowing: boolean;
  incrementDownload: () => Promise<unknown> | unknown;
  onUpgradeRequired: () => void;
};

export const useExcerptImageExport = ({
  previewRef,
  active,
  fontFamily,
  exportBgFallback,
  downloadsAtLimit,
  isOverflowing,
  incrementDownload,
  onUpgradeRequired,
}: Args) => {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!previewRef.current || !active) return;
    if (isOverflowing) {
      toast.error("Excerpt is too long to fit in a 1200 × 1800 image. Trim the text and try again.");
      return;
    }
    if (downloadsAtLimit) {
      onUpgradeRequired();
      return;
    }
    setDownloading(true);
    try {
      ensureFont(fontFamily);
      try {
        await (document as Document & { fonts?: { load: (s: string) => Promise<unknown>; ready: Promise<unknown> } }).fonts?.load(`500 60px "${fontFamily}"`);
      } catch {
        // Font loading is best-effort; fall back to whatever's available.
      }
      try {
        await (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts?.ready;
      } catch {
        // Best-effort.
      }

      const PIXEL_RATIO = 2;
      const exportCanvas = await toCanvas(previewRef.current, {
        pixelRatio: PIXEL_RATIO,
        cacheBust: true,
        backgroundColor: exportBgFallback,
        skipFonts: true,
        fontEmbedCSS: "",
      });

      const exportCtx = exportCanvas.getContext("2d");
      if (!exportCtx) throw new Error("Failed to render image");
      // Defensive reset: some upstream paths can leave the context in
      // `source-in` / partial-alpha state, which produces a transparent canvas
      // that JPEG then encodes as solid black.
      exportCtx.globalCompositeOperation = "source-over";
      exportCtx.globalAlpha = 1;

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = exportCanvas.width;
      finalCanvas.height = exportCanvas.height;
      const finalCtx = finalCanvas.getContext("2d");
      if (!finalCtx) throw new Error("Failed to render image");
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = "high";
      finalCtx.globalCompositeOperation = "source-over";
      finalCtx.globalAlpha = 1;
      // JPEG has no alpha channel — paint a fully opaque background first so
      // any transparent pixels from the snapshot don't encode as black.
      finalCtx.fillStyle = exportBgFallback;
      finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      finalCtx.drawImage(exportCanvas, 0, 0, finalCanvas.width, finalCanvas.height);

      const dataUrl = finalCanvas.toDataURL("image/jpeg", 1);
      const stamped = embedExcerptusMetadata(dataUrl);
      const a = document.createElement("a");
      a.href = stamped;
      a.download = `excerptus-${Date.now()}.jpg`;
      a.click();
      await incrementDownload();
    } catch (e: unknown) {
      toast.error((e as Error)?.message ?? "Failed to render");
    } finally {
      setDownloading(false);
    }
  };

  return { download, downloading };
};
